/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@mui/material'
import commonStyles from 'commonStyles/index.module.css'
import { METRIC_ENDPOINT_RANK, METRIC_ENDPOINT_WEIGHT, RANKED_COLOR } from 'constant'
import { useActions, useAppSelector } from 'hooks'
import { ForwardedRef, forwardRef, useMemo, useState } from 'react'
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import {
  calculateStringWidth,
  calculateWeightedGlobalBarChart,
  generateGlobalBarChartData,
  isAllMetricValueEmpty,
  isAllPillarValueEmpty,
} from 'utils'
import { CustomizedDot, handleMouseMove, onClickDotFunc, renderCustomAxisTick } from './utils'

export interface SlopeChartProps {
  is4Downloaded?: boolean
}

const SlopeChartCommon = forwardRef(({ is4Downloaded }: SlopeChartProps, ref: ForwardedRef<HTMLDivElement>) => {
  const configuration = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  // const metricGroup = calculateMetricGroupForTable(metricGroups, { metricValue: configuration.metricValue })
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)

  // const renderChartData = calculateWeightedGlobalBarChart(
  //   metricGroups,
  //   configuration.metricValue,
  //   countryData,
  //   configuration.yearFilter.currentYear,
  // )
  // const slopeData = useMemo<Country[]>(() => {
  //   return calculateCountryDataFromMetricValue(metricGroup, renderChartData, {
  //     globalYear: configuration.yearFilter.currentYear,
  //     metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //     metricEndpointRank: METRIC_ENDPOINT_RANK,
  //     metricValue: configuration.metricValue,
  //   })
  // }, [countryData, metricGroup, renderChartTimes])

  // const data = useMemo<SlopeData>(() => { // old implementation
  //   return generateSlopeChartData(
  //     metricGroup,
  //     slopeData,
  //     // countryData,
  //     {
  //       yearRange: configuration.yearFilter.yearRange,
  //       metricEndpointRank: METRIC_ENDPOINT_RANK,
  //       metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //       metricValue: configuration.metricValue,
  //     },
  //   )
  // }, [slopeData, metricGroup])

  const data = useMemo(() => {
    if (metricGroups.length === 0 || countryData.length === 0 || configuration.yearFilter.yearRange.length === 0) {
      return []
    }
    let list: any = []
    try {
      list = [...configuration?.yearFilter?.yearRange]
        ?.sort((a, b) => b - a)
        ?.map((year) => {
          const renderChartDataBar = calculateWeightedGlobalBarChart(
            metricGroups,
            configuration.metricValue,
            countryData,
            year,
          )
          const dataReturn = generateGlobalBarChartData(metricGroups, renderChartDataBar, {
            globalYear: year,
            metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
            metricEndpointRank: METRIC_ENDPOINT_RANK,
            metricValue: configuration.metricValue,
          })
          const rankObject: Record<string, string | number> = {
            year: year?.toString(),
          }
          dataReturn?.forEach((el, index) => {
            rankObject[el.countryName] = index + 1
          })
          return rankObject
        })
    } catch (error) {}
    return list
  }, [countryData, metricGroups, renderChartTimes, configuration.yearFilter.yearRange, renderChartTimes])

  const countryTicks = useMemo<{ countryName: string; rank: number; color: string }[]>(() => {
    return Object.entries(data[0])
      .filter(([key]) => key !== 'year')
      .map(([key, value]) => ({ countryName: key, rank: value as number }))
      .map((el, index) => ({ ...el, color: RANKED_COLOR[index % 5] }))
  }, [data])
  const { setRanking, setViewType, setCountry, setCurrentYear } = useActions()
  const [hoveredCountry, setHoveredCountry] = useState('')
  const [activeCoordinate, setActiveCoordinate] = useState({ x: 0, y: 0 })

  const xAxisHeight = useMemo(() => {
    if (!countryTicks || countryTicks.length === 0) return 0
    const countryNameWidths = countryTicks.map((item) =>
      calculateStringWidth(item.countryName, 'normal 10px "CircularStdMedium"'),
    )
    const maxWidth = Math.max(...countryNameWidths)

    return maxWidth + 5
  }, [data, countryTicks])

  const lastCountryNameHeight = useMemo(() => {
    if (!countryTicks || countryTicks.length === 0) return 0
    return calculateStringWidth(countryTicks.slice(-1)[0]!.countryName, 'normal 10px "CircularStdMedium"')
  }, [data, countryTicks])

  const CHART_WIDTH = 40 * countryTicks.length
  return (
    <Box
      sx={{
        overflowY: 'hidden',
        overflowX: is4Downloaded ? 'hidden' : 'auto',
        height: configuration.yearFilter.yearRange.length * 120,
      }}
      className={is4Downloaded ? undefined : commonStyles['scrollbar-common']}
      ref={ref}
    >
      {!isAllPillarValueEmpty(metricGroups) && !isAllMetricValueEmpty(metricGroups, configuration.metricValue) && (
        <ResponsiveContainer width={CHART_WIDTH} height='100%'>
          <LineChart
            layout='vertical'
            data={data}
            margin={{
              top: 20,
              right: lastCountryNameHeight,
              left: 20,
              bottom: 25,
            }}
            onMouseMove={(state) => handleMouseMove(state, !!hoveredCountry, setActiveCoordinate, CHART_WIDTH)}
          >
            <defs>
              <filter id={`lineShadow`} x='-50%' y='-50%' width='200%' height='200%' filterUnits='userSpaceOnUse'>
                <feOffset result='offOut' in='SourceGraphic' dx='5' dy='5' />
                <feColorMatrix
                  result='matrixOut'
                  in='offOut'
                  type='matrix'
                  values='0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0.5 0'
                />
                <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='3' />
                <feBlend in='SourceGraphic' in2='blurOut' mode='normal' />
              </filter>
            </defs>
            <XAxis
              height={xAxisHeight}
              type='number'
              domain={[1, 'dataMax']}
              tickCount={countryTicks.length}
              interval={0}
              orientation='top'
              axisLine={false}
              tickLine={false}
              tick={(props) => renderCustomAxisTick(props, countryTicks)}
            />
            <YAxis
              tick={{ dx: -15, fill: 'black' }}
              type='category'
              dataKey='year'
              axisLine={false}
              tickLine={false}
              allowDuplicatedCategory={false}
              interval='preserveStartEnd'
            />
            {/* <Tooltip content={<CustomTooltip show={!!hoveredCountry} />} position={activeCoordinate} /> */}
            {countryTicks.map((countryTick) => {
              return (
                <Line
                  key={countryTick.countryName}
                  dataKey={countryTick.countryName}
                  stroke={countryTick.color}
                  dot={
                    <CustomizedDot
                      value={countryTick.rank}
                      onMouseOver={() => {
                        setHoveredCountry(countryTick.countryName)
                      }}
                      onMouseOut={() => {
                        setHoveredCountry('')
                      }}
                      isHovered={hoveredCountry === countryTick.countryName}
                      onClickFunc={(yearIndex: number) =>
                        onClickDotFunc(yearIndex, countryTick, configuration.yearFilter.yearRange, countryData, {
                          setViewType,
                          setRanking,
                          setCurrentYear,
                          setCountry,
                        })
                      }
                    />
                  }
                  activeDot={
                    <CustomizedDot
                      value={countryTick.rank}
                      activeStroke={countryTick.color}
                      onMouseOver={() => {
                        setHoveredCountry(countryTick.countryName)
                      }}
                      onMouseOut={() => {
                        setHoveredCountry('')
                      }}
                      isHovered={hoveredCountry === countryTick.countryName}
                      onClickFunc={(yearIndex: number) =>
                        onClickDotFunc(yearIndex, countryTick, configuration.yearFilter.yearRange, countryData, {
                          setViewType,
                          setRanking,
                          setCurrentYear,
                          setCountry,
                        })
                      }
                    />
                  }
                  filter={hoveredCountry === countryTick.countryName ? `url(#lineShadow)` : undefined}
                  isAnimationActive={false}
                />
              )
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  )
})

SlopeChartCommon.displayName = 'SlopeChartComponent'
export default SlopeChartCommon
